/** Description strings used to assign building permit slots (must match filter logic). */
export const BUILDING_PERMIT_DESCRIPTIONS = {
    site_map: "Building Permit - site map",
    notice_to_vacate: "Building Permit - notice to vacate (sow)",
    certificates: "Building Permit - certificates",
    cfi_certificate: "Building Permit - certificate of final inspection",
    other: "Building Permit - other",
};
/**
 * Derive the four building permit slots from site documents by matching
 * description (same logic as admin JobDetailTab "Attach Documents").
 * Each slot contains all matching documents, sorted by createdAt.
 */
export function getBuildingPermitSlotsFromSiteDocuments(siteDocuments) {
    const buildingPermitDocuments = siteDocuments.filter((doc) => doc.description &&
        doc.description.toLowerCase().includes("building permit"));
    const mapped = {
        site_map: [],
        notice_to_vacate: [],
        certificates: [],
        cfi_certificate: [],
        other: [],
    };
    buildingPermitDocuments.forEach((doc) => {
        const desc = doc.description?.toLowerCase() || "";
        if (desc.includes("site map"))
            mapped.site_map.push(doc);
        else if (desc.includes("notice to vacate") ||
            desc.includes("notice to vacate (sow)"))
            mapped.notice_to_vacate.push(doc);
        else if (desc.includes("certificate of final inspection") ||
            desc.includes("cfi"))
            mapped.cfi_certificate.push(doc);
        else if (desc.includes("certificates"))
            mapped.certificates.push(doc);
        else if (desc.includes("other"))
            mapped.other.push(doc);
    });
    const sortByCreated = (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    mapped.site_map.sort(sortByCreated);
    mapped.notice_to_vacate.sort(sortByCreated);
    mapped.certificates.sort(sortByCreated);
    mapped.cfi_certificate.sort(sortByCreated);
    mapped.other.sort(sortByCreated);
    return mapped;
}
/**
 * Map JobDocument (jobs API) by documentType to building permit slots.
 * Each slot contains all matching documents, sorted by createdAt.
 */
export function getBuildingPermitSlotsFromJobDocuments(jobDocuments) {
    const mapped = {
        site_map: [],
        notice_to_vacate: [],
        certificates: [],
        cfi_certificate: [],
        other: [],
    };
    jobDocuments.forEach((doc) => {
        const t = (doc.documentType ?? "").toLowerCase();
        if (t === "site_map")
            mapped.site_map.push(doc);
        else if (t === "notice_to_vacate")
            mapped.notice_to_vacate.push(doc);
        else if (t === "certificates")
            mapped.certificates.push(doc);
        else if (t === "cfi_certificate")
            mapped.cfi_certificate.push(doc);
        else if (t === "other")
            mapped.other.push(doc);
    });
    const sortByCreated = (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    mapped.site_map.sort(sortByCreated);
    mapped.notice_to_vacate.sort(sortByCreated);
    mapped.certificates.sort(sortByCreated);
    mapped.cfi_certificate.sort(sortByCreated);
    mapped.other.sort(sortByCreated);
    return mapped;
}
/**
 * Derive the four building permit slots from site, job (documents API),
 * quote, and job (jobs API) documents. Precedence: site > job (documents API)
 * > quote > job (jobs API).
 */
export function getBuildingPermitSlotsFromDocuments(siteDocuments, jobDocuments, jobDocumentsFromJobsApi, quoteDocuments) {
    const siteSlots = getBuildingPermitSlotsFromSiteDocuments(siteDocuments);
    const jobSlots = getBuildingPermitSlotsFromSiteDocuments(jobDocuments);
    const quoteSlots = quoteDocuments?.length
        ? getBuildingPermitSlotsFromSiteDocuments(quoteDocuments)
        : {
            site_map: [],
            notice_to_vacate: [],
            certificates: [],
            cfi_certificate: [],
            other: [],
        };
    const jobApiSlots = jobDocumentsFromJobsApi
        ? getBuildingPermitSlotsFromJobDocuments(jobDocumentsFromJobsApi)
        : {
            site_map: [],
            notice_to_vacate: [],
            certificates: [],
            cfi_certificate: [],
            other: [],
        };
    const pick = (s, j, q, jApi) => (s.length > 0 ? s : j.length > 0 ? j : q.length > 0 ? q : jApi);
    return {
        site_map: pick(siteSlots.site_map, jobSlots.site_map, quoteSlots.site_map, jobApiSlots.site_map),
        notice_to_vacate: pick(siteSlots.notice_to_vacate, jobSlots.notice_to_vacate, quoteSlots.notice_to_vacate, jobApiSlots.notice_to_vacate),
        certificates: pick(siteSlots.certificates, jobSlots.certificates, quoteSlots.certificates, jobApiSlots.certificates),
        cfi_certificate: pick(siteSlots.cfi_certificate, jobSlots.cfi_certificate, quoteSlots.cfi_certificate, jobApiSlots.cfi_certificate),
        other: pick(siteSlots.other, jobSlots.other, quoteSlots.other, jobApiSlots.other),
    };
}
